'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link2, Copy, Check, Plus, Trash2, ExternalLink, Loader2, MousePointerClick } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface Campaign {
  id: string;
  name: string;
}

interface TrackableLink {
  id: string;
  campaign_id: string;
  campaign_name: string;
  original_url: string;
  label: string;
  short_code: string;
  created_at: string;
  click_count?: number;
}

interface TrackableLinkGeneratorProps {
  campaigns: Campaign[];
}

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://neetnaviga9114.builtwithrocket.new';

function generateShortCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

export default function TrackableLinkGenerator({ campaigns }: TrackableLinkGeneratorProps) {
  const supabase = useMemo(() => createClient(), []);

  const [links, setLinks] = useState<TrackableLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [form, setForm] = useState({
    campaign_id: campaigns[0]?.id ?? '',
    original_url: '',
    label: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trackable_links')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Fetch click counts
      const { data: clicks } = await supabase
        .from('link_clicks')
        .select('trackable_link_id');

      const clickMap: Record<string, number> = {};
      (clicks ?? []).forEach((c: { trackable_link_id: string }) => {
        clickMap[c.trackable_link_id] = (clickMap[c.trackable_link_id] ?? 0) + 1;
      });

      setLinks(
        (data ?? []).map((l: TrackableLink) => ({
          ...l,
          click_count: clickMap[l.id] ?? 0,
        }))
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load links';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.campaign_id) errors.campaign_id = 'Select a campaign';
    if (!form.label.trim()) errors.label = 'Link label is required';
    if (!form.original_url.trim()) errors.original_url = 'Destination URL is required';
    else {
      try {
        new URL(form.original_url);
      } catch {
        errors.original_url = 'Enter a valid URL (include https://)';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const campaign = campaigns.find((c) => c.id === form.campaign_id);
      const short_code = generateShortCode();
      const { error } = await supabase.from('trackable_links').insert({
        campaign_id: form.campaign_id,
        campaign_name: campaign?.name ?? form.campaign_id,
        original_url: form.original_url,
        label: form.label,
        short_code,
      });
      if (error) throw error;
      toast.success('Trackable link generated!');
      setForm({ campaign_id: form.campaign_id, original_url: '', label: '' });
      setFormErrors({});
      fetchLinks();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to generate link';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (link: TrackableLink) => {
    const trackUrl = `${SITE_URL}/api/track/${link.short_code}`;
    try {
      await navigator.clipboard.writeText(trackUrl);
      setCopiedId(link.id);
      toast.success('Trackable link copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('trackable_links').delete().eq('id', id);
      if (error) throw error;
      toast.success('Link deleted');
      fetchLinks();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to delete link';
      toast.error(msg);
    }
  };

  const inputCls = (err?: string) =>
    `w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${err ? 'border-red-400' : 'border-border'}`;

  return (
    <div className="space-y-6">
      {/* Generator Form */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Link2 size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Generate Trackable Link</h3>
            <p className="text-xs text-muted-foreground">Wrap any URL to track clicks per campaign and lead</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Campaign <span className="text-red-500">*</span></label>
            <select
              value={form.campaign_id}
              onChange={(e) => { setForm((f) => ({ ...f, campaign_id: e.target.value })); setFormErrors((er) => ({ ...er, campaign_id: '' })); }}
              className={inputCls(formErrors.campaign_id)}
            >
              <option value="">Select campaign…</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {formErrors.campaign_id && <p className="text-xs text-red-500 mt-1">{formErrors.campaign_id}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Link Label <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => { setForm((f) => ({ ...f, label: e.target.value })); setFormErrors((er) => ({ ...er, label: '' })); }}
              placeholder="e.g. Brochure Download"
              className={inputCls(formErrors.label)}
            />
            {formErrors.label && <p className="text-xs text-red-500 mt-1">{formErrors.label}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Destination URL <span className="text-red-500">*</span></label>
            <input
              type="url"
              value={form.original_url}
              onChange={(e) => { setForm((f) => ({ ...f, original_url: e.target.value })); setFormErrors((er) => ({ ...er, original_url: '' })); }}
              placeholder="https://example.com/brochure.pdf"
              className={inputCls(formErrors.original_url)}
            />
            {formErrors.original_url && <p className="text-xs text-red-500 mt-1">{formErrors.original_url}</p>}
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            onClick={handleGenerate}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Generate Link
          </button>
        </div>
      </div>

      {/* Links Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Generated Links</h3>
          {loading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
        </div>

        {links.length === 0 && !loading ? (
          <div className="py-12 text-center">
            <Link2 size={32} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No trackable links yet. Generate your first one above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Label</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Campaign</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Trackable URL</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Destination</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-muted-foreground">Clicks</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {links.map((link) => {
                  const trackUrl = `${SITE_URL}/api/track/${link.short_code}`;
                  return (
                    <tr key={link.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground">{link.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{link.campaign_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-muted-foreground truncate max-w-[180px] block">{trackUrl}</span>
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={link.original_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1 max-w-[160px] truncate"
                        >
                          {link.original_url}
                          <ExternalLink size={10} className="shrink-0" />
                        </a>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                          <MousePointerClick size={12} className="text-primary" />
                          {link.click_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleCopy(link)}
                            className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                            title="Copy trackable URL"
                          >
                            {copiedId === link.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                          <button
                            onClick={() => handleDelete(link.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                            title="Delete link"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
